import { useEffect, useState } from 'react';
import { api, type DashboardSummary } from '../lib/api';
import s from './DashboardPage.module.css';

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                     */
/* -------------------------------------------------------------------------- */

function passRateClass(rate: number): string {
  if (rate >= 90) return s.kpiValueSuccess;
  if (rate >= 70) return s.kpiValueWarning;
  return s.kpiValueDanger;
}

/* -------------------------------------------------------------------------- */
/*  DashboardPage                                                              */
/* -------------------------------------------------------------------------- */

export default function DashboardPage() {
  const [data, setData] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    api.dashboard
      .summary()
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load dashboard');
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return <div className={s.loading}>Loading dashboard&hellip;</div>;
  }

  if (error) {
    return <div className={s.error}>{error}</div>;
  }

  const summary = data ?? {
    domainCount: 0,
    totalEmails: 0,
    passRate: 0,
    failCount: 0,
    topSources: [],
    trendData: [],
  };

  // Usage placeholder
  const reportsUsed = 82;
  const reportsLimit = 100;
  const usagePct = Math.min((reportsUsed / reportsLimit) * 100, 100);
  const isAtLimit = reportsUsed >= reportsLimit;

  // Derive reports in 7d from trend data
  const reportsCount = summary.trendData.reduce(
    (acc, d) => acc + d.pass + d.fail,
    0,
  );

  return (
    <>
      {/* Page header */}
      <div className={s.header}>
        <h1 className={s.title}>Dashboard</h1>
      </div>

      {/* KPI cards */}
      <div className={s.kpiGrid}>
        <div className={s.kpiCard}>
          <div className={s.kpiLabel}>Domains Monitored</div>
          <div className={s.kpiValue}>{summary.domainCount}</div>
        </div>
        <div className={s.kpiCard}>
          <div className={s.kpiLabel}>Reports (7d)</div>
          <div className={s.kpiValue}>{reportsCount || 0}</div>
        </div>
        <div className={s.kpiCard}>
          <div className={s.kpiLabel}>Messages (7d)</div>
          <div className={s.kpiValue}>{summary.totalEmails.toLocaleString()}</div>
        </div>
        <div className={s.kpiCard}>
          <div className={s.kpiLabel}>Pass Rate</div>
          <div className={passRateClass(summary.passRate)}>
            {summary.passRate.toFixed(1)}%
          </div>
        </div>
      </div>

      {/* Usage bar */}
      <div className={s.usageCard}>
        <div className={s.usageHeader}>
          <span className={s.usageTitle}>Reports this week</span>
          <span className={s.usageCount}>
            {reportsUsed} / {reportsLimit}
          </span>
        </div>
        <div className={s.usageBarTrack}>
          <div
            className={s.usageBarFill}
            style={{
              width: `${usagePct}%`,
              backgroundColor: isAtLimit ? 'var(--danger)' : undefined,
            }}
          />
        </div>
        {isAtLimit && (
          <div className={s.usageWarning}>
            You have reached your weekly report limit. Upgrade your plan to
            continue receiving reports.
          </div>
        )}
      </div>

      {/* Sections */}
      <div className={s.sectionsGrid}>
        <div className={s.section}>
          <div className={s.sectionTitle}>Recent Reports</div>
          <div className={s.emptyState}>No data yet</div>
        </div>
        <div className={s.section}>
          <div className={s.sectionTitle}>Top Sources</div>
          <div className={s.emptyState}>No data yet</div>
        </div>
      </div>
    </>
  );
}
